/**
 * useDebouncedCallback — coalesces rapid calls into one after `delayMs` of quiet.
 * Used for text/number inputs so we preview once the value settles, not per keystroke.
 * Option selection does NOT use this — it previews immediately.
 */
import { useEffect, useMemo, useRef } from 'react';

export function useDebouncedCallback<A extends unknown[]>(
  fn: (...args: A) => void,
  delayMs: number,
): (...args: A) => void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return useMemo(() => {
    return (...args: A): void => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => fnRef.current(...args), delayMs);
    };
  }, [delayMs]);
}
